<template>
  <div
    class="size-guide"
    :class="{ 'size-guide--drawer': isDrawer }"
  >
    <template v-if="activeSizeGuideValue && state == 'ready'">
      <div class="size-guide__heading">
        <h2
          class="size-guide__title text-body-2xs-regular"
          v-text="title"
        />
        <template v-if="!isConversionGuide">
          <div
            class="size-guide__dropdown form-group"
          >
            <label
              class="visually-hidden"
              for="size-guides"
              v-text="sizeGuideLabel"
            />

            <select
              id="size-guides"
              v-model="activeSizeGuideValue"
              class="size-guide__select"
              @change="setLocationHistory"
            >
              <option
                v-for="option of getSizeGuideValues"
                :key="option.value"
                :value="option.value"
                v-text="option.label"
              />
            </select>
          </div>

          <ul
            class="size-guide__unit-buttons"
            :class="{ [$class('hidden')]: shouldHide }"
          >
            <li>
              <btn
                class="size-guide__unit-button"
                :class="{[$class('active')]: imperialUnits }"
                modifiers="text inline"
                label-class="text-body-2xs-regular"
                :label="$string('product.size_guide.imperial_abbr')"
                :aria-label="$string('product.size_guide.imperial_label')"
                @click="toggleUnitSystem"
              />
            </li>

            <li>
              <btn
                class="size-guide__unit-button"
                :class="{[$class('active')]: !imperialUnits }"
                modifiers="text inline"
                label-class="text-body-2xs-regular"
                :label="$string('product.size_guide.metric_abbr')"
                :aria-label="$string('product.size_guide.metric_label')"
                @click="toggleUnitSystem"
              />
            </li>
          </ul>
        </template>
      </div>

      <div
        class="size-guide__table-wrapper"
        tabindex="0"
      >
        <table-snippet
          block-class="size-guide"
          is-first-row-heading
          :table-csv="activeSizeGuide.table"
        />
      </div>

      <a
        v-if="renderCta"
        class="size-guide__table-cta"
        :href="sizeGuidePageUrl"
        v-text="$string('product.size_guide.view_full_size_guide')"
      />
    </template>
  </div>
</template>

<script>

/**
 * Vue: Size guide (size-guide)
 * -----------------------------------------------------------------------------
 * Size guide table.
 * @param {Boolean} conversionGuide - International conversion guide CSV
 * @param {Boolean} isConversionGuide - Whether this is a conversion guide
 * @param {Boolean} isDrawer - If this is the size guide drawer
 * @param {String} productType - Product type from which drawer was opened
 * @param {String} sizeGuidePageUrl - URL to full page
 *
 */
import { formatSizeGuides } from '~/format/format-size-guides'
import metaobjectsQuery from '~graphql/metaobjects.gql'

import TableSnippet from '~async/table-snippet/table-snippet'
import Btn from '~global/btn/btn'

export default {
  name: 'SizeGuide',

  components: {
    Btn,
    TableSnippet,
  },

  props: {
    conversionGuide: {
      type: [String, null],
      default: null,
    },
    isConversionGuide: {
      type: Boolean,
      default: false,
    },
    isDrawer: {
      type: Boolean,
      default: false,
    },
    productType: {
      type: [String, null],
      default: null,
    },
    sizeGuidePageUrl: {
      type: String,
      default: null,
    },
    isoCode: {
      type: String,
      default: 'US',
    },
  },

  data() {
    return {
      activeSizeGuideValue: null,
      activeSizeGuide: null,
      imperialUnits: cnvs?.store?.country === 'US',
      sizeGuides: null,
      state: 'loading',
      selected: null,
      loadedUrl: false,
    }
  },

  computed: {

    /**
     * Computes size guide option values
     * @returns {Array}
     */
    getSizeGuideValues() {
      return this.sizeGuides.map(sizeGuide => {
        return {
          label: this.$string(
            'product.size_guide.title',
            { replace: { title: sizeGuide.title } },
          ),
          value: sizeGuide.title,
        }
      })
    },

    /**
     * Computes Size guides
     * @returns {Array}
     */
    getSizeGuides() {
      return this.sizeGuides.map(sizeGuide => {
        return {
          label: sizeGuide.title,
          value: this.formatSelectSizeGuide(sizeGuide),
        }
      })
    },

    /**
     * Compute general size guide to use as a fallback.
     * @returns {Object}
     */
    generalSizeGuide() {
      return this.sizeGuides?.find(sizeGuide => {
        return sizeGuide.generalSizeGuide
      })
    },

    /**
     * Compute title to render.
     * @returns {String}
     */
    title() {
      return this.isConversionGuide
        ? this.$string('product.size_guide.conversion_guide_title')
        : this.$string(
          'product.size_guide.title',
          { replace: { title: this.activeSizeGuideValue } },
        )
    },

    /**
     * Compute whether to render the CTA to the full size guide page.
     * @returns {Boolean}
     */
    renderCta() {
      return this.isDrawer && this.sizeGuidePageUrl && !this.isConversionGuide
    },

    /**
     * Determines if unit buttons should be displayed
     * @returns {Boolean}
     */
    shouldHide() {
      const selectedTitle = this.activeSizeGuideValue?.toLowerCase()
      return selectedTitle === 'shoes' || selectedTitle === 'dickeys'
    },

    /**
     * Compute the full size guide label with accessibility text
     */
    sizeGuideLabel() {
      const label = this.$string('product.size_guide.size_guide_select')
      const accessibility = this.$string('accessibility.size_guide.content_change_label')

      return `${label} - ${accessibility}`
    },
  },

  watch: {

    /**
     * Watch for switch between whether this is a conversion guide.
     */
    isConversionGuide() {
      this.setInitialGuide()
    },

    /**
     * Watch for switch between imperial & metric units to convert.
     */
    imperialUnits() {
      if (!this.activeSizeGuide || !this.activeSizeGuide.table) {
        return
      }

      if (this.imperialUnits) {
        const matchedSizeGuide = this.sizeGuides?.find(sizeGuide => {
          return sizeGuide.title === this.activeSizeGuide.title
        })

        this.activeSizeGuide = matchedSizeGuide
        this.state = 'ready'
        return
      }

      const value = this.activeSizeGuide

      this.activeSizeGuide = this.convertToMetric(this.activeSizeGuide)
      this.activeSizeGuide.value = value
      this.state = 'ready'
    },

    /**
     * Watch product type to set active guide.
     */
    productType() {
      this.setActiveSizeGuide()
    },

    /**
     * Watch size guides to set active guide.
     */
    sizeGuides() {
      this.setActiveSizeGuide()
    },

    /**
     * Watch active size guide value to set active guide and guide url.
     */
    activeSizeGuideValue() {
      const newActive = this.sizeGuides.filter(sizeGuide => {
        return sizeGuide.title === this.activeSizeGuideValue
      })

      if (newActive) {
        this.activeSizeGuide = this.formatSelectSizeGuide(newActive[0])
      }

      if (this.activeSizeGuideValue === '' || !this.activeSizeGuideValue) {
        this.activeSizeGuideValue = this.generalSizeGuide?.title
      }
    },
  },

  mounted() {
    const self = this
    this.setInitialGuide()

    window.addEventListener('popstate', () => {
      self.activeSizeGuideValue = self.getGuideFromUrl()
    })
  },

  methods: {

    /**
     * Filter size guides based on geo location
     * @param {Array} response Formatted array of size guides
     * @returns {Array} Filtered array of size guides based on geo location
     */
    geoFilterSizeGuides(response) {
      const reducedArr = response.map((object) => {
        return {
          generalSizeGuide: object.generalSizeGuide,
          productTypes: object.productTypes,
          title: object.title,
          urlPath: object.urlPath,
        }
      })

      response.forEach((object, idx) => {
        const tableArr = []
        object.table?.split('\n').forEach(row => {
          const cellArray = row.split(',')[0].split(' ')
          if (cellArray.length === 1 ||
            cellArray[0].length >= 3 ||
            (cellArray[0].toLowerCase() === this.isoCode.toLowerCase() ||
              cellArray[0].toLowerCase() === 'us')) {
            tableArr.push(row)
          }
        })

        reducedArr[idx].table = tableArr.join('\n')
      })

      return reducedArr
    },

    /**
     * Get the size guide from the URL
     * @returns {String} Size guide to display
     */
    getGuideFromUrl() {
      const href = window.location.href
      const guide = href.slice(href.lastIndexOf('/') + 1, href.length)
      return this.sizeGuides.filter((sizeGuide) => sizeGuide.urlPath === guide)[0]?.title
    },

    /**
     * Sets the location history in the browser base on the size guide selected
     */
    setLocationHistory(option) {
      if (!this.isDrawer) {
        const href = window.location.href
        const newHref = href.slice(0, href.lastIndexOf('/'))

        window.history.pushState({}, '', `${newHref}/${this.sizeGuides
          .filter(sizeGuide => sizeGuide.urlPath
            .indexOf(option.value.toLowerCase().split(' ')[0]) > -1)[0].urlPath}`)
      }
    },

    /**
     * Convert size table units to metric centimeters and return updated object.
     * @param {Object} sizeGuide
     * @return {Object}
     */
    convertToMetric(sizeGuide) {
      if (!sizeGuide) {
        return {}
      }

      const cmPerInch = 2.54
      const tableCsv = sizeGuide.table
      const tableRows = tableCsv.split('\n')

      const convertedTableArray = tableRows.map((row, rowIndex) => {
        if (rowIndex === 0) {
          return row
        }

        const cellArray = row.split(',')[0]?.split(' ')

        if (cellArray[0]?.toLowerCase() !== 'us' &&
          cellArray[1]?.toLowerCase() === 'size') {
          return row
        }

        return row
          .split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/)
          .map((item, itemIndex) => {
            if (itemIndex === 0) {
              return item
            }

            const number = parseFloat(item)

            if (!number) {
              return item
            }

            return this.imperialUnits
              ? this.roundUnitValue(item / cmPerInch)
              : this.roundUnitValue(item * cmPerInch)
          })
      })

      const convertedTableString = convertedTableArray.join('\n')

      return {
        ...sizeGuide,
        table: convertedTableString,
      }
    },

    /**
     * Format size guide object for select value. Convert units if necessary.
     * @param {Object} sizeGuide
     * @returns {Object}
     */
    formatSelectSizeGuide(sizeGuide) {
      return this.imperialUnits
        ? sizeGuide
        : this.convertToMetric(sizeGuide)
    },

    /**
     * Query size guides.
     */
    async querySizeGuides() {
      this.state = 'loading'

      try {
        const response = await cnvs.Query({
          query: metaobjectsQuery,
          variables: {
            type: 'size_guide',
          },
        })

        const formattedSizeGuides = formatSizeGuides(response)
        this.sizeGuides = this.geoFilterSizeGuides(formattedSizeGuides)

        await this.$nextTick()
        this.activeSizeGuideValue = this.getGuideFromUrl()
      } catch (error) {
        cnvs.ReportError('Failed to load metaobjects', error)
      }
    },

    /**
     * Set active guide from array of size guides.
     */
    setActiveSizeGuide() {
      if (!this.productType) {
        if (this.imperialUnits) {
          this.activeSizeGuide = this.generalSizeGuide
          this.activeSizeGuideValue = this.generalSizeGuide?.title
          this.state = 'ready'
          return
        }

        this.activeSizeGuide = this.convertToMetric(this.generalSizeGuide)
        this.state = 'ready'
      }

      if (this.productType) {
        const matchedSizeGuide = this.sizeGuides?.find(sizeGuide => {
          return sizeGuide.productTypes.includes(this.productType)
        })

        this.activeSizeGuideValue = matchedSizeGuide?.title

        if (this.imperialUnits) {
          this.activeSizeGuide = matchedSizeGuide || this.generalSizeGuide
          this.state = 'ready'
          return
        }

        this.activeSizeGuide = this.matchedSizeGuide
          ? this.convertToMetric(matchedSizeGuide)
          : this.convertToMetric(this.generalSizeGuide)
        this.state = 'ready'
      }
    },

    /**
     * Set initial guide on mounted, based on props passed.
     */
    setInitialGuide() {
      if (this.conversionGuide && this.isConversionGuide) {
        this.activeSizeGuide = { table: this.conversionGuide }
        this.state = 'ready'
        return
      }

      this.querySizeGuides()
    },

    /**
     * Round converted unit value to the nearest one-decimal point.
     * @param {Number} units - Value in inches or cm.
     */
    roundUnitValue(units) {
      return parseFloat(units.toFixed(1))
    },

    /**
     * Toggle units between imperial (inch) and metric (cm) systems.
     */
    toggleUnitSystem() {
      this.imperialUnits = !this.imperialUnits
    },
  },
}
</script>
